import React from "react";
import { Layout, Typography, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { HeaderOptions } from "../../../types/dashboard";

const { Header } = Layout;
const { Title, Text } = Typography;

type Props = {
  title: string;
  subtitle: string;
  editingTitle: boolean;
  headerOptions: HeaderOptions;
  setTitle: (v: string) => void;
  setEditingTitle: (v: boolean) => void;
  onEdit?: () => void;
  actions?: React.ReactNode;
  readOnly?: boolean;
};

const HeaderSection: React.FC<Props> = ({
  title,
  subtitle,
  editingTitle,
  headerOptions,
  setTitle,
  setEditingTitle,
  onEdit,
  actions,
  readOnly = false,
}) => {
  const rightSide =
    headerOptions.headerImageAlign === "right" ||
    headerOptions.logoAlign === "right";

  const leftSide =
    headerOptions.headerImageAlign === "left" ||
    headerOptions.logoAlign === "left";

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "https://developmentapi.kapinsights.com";

  const getAbsoluteImageUrl = (path?: string | null) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API_BASE_URL}${path}`;
  };

  return (
    <Header
      style={{
        background: headerOptions.backgroundColor,
        padding: `12px ${headerOptions.paddingRight}px 12px ${headerOptions.paddingLeft}px`,
        position: "sticky",
        top: 0,
        zIndex: 100,
        height: "auto",
        minHeight: 144,
        borderRadius: "12px 12px 0 0",
        border: `${headerOptions.borderWidth}px solid ${headerOptions.borderColor}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
      }}
    >
      {/* Internal badge + edit icon row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#B3BCEC",
            borderRadius: 999,
            height: 24,
            padding: "0 10px",
            fontSize: 13,
            fontWeight: 600,
            color: "#4A1C96",
            lineHeight: "24px",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#4A1C96",
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          Internal
        </span>
        {!readOnly && (
          <EditOutlined
            className="dashboard-header-edit-icon"
            onClick={() => onEdit ? onEdit() : setEditingTitle(true)}
            style={{
              fontSize: 18,
              color: "rgba(255,255,255,0.85)",
              cursor: "pointer",
            }}
          />
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {leftSide && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {headerOptions.headerImageDataUrl &&
                headerOptions.headerImageAlign === "left" && (
                  <img
                    src={getAbsoluteImageUrl(headerOptions.headerImageDataUrl)}
                    alt="header"
                    style={{
                      width: headerOptions.headerImageWidth,
                      height: "auto",
                      borderRadius: headerOptions.headerImageRadius,
                      display: "block",
                    }}
                  />
                )}

              {headerOptions.logoUrl && headerOptions.logoAlign === "left" && (
                <img
                  src={getAbsoluteImageUrl(headerOptions.logoUrl)}
                  alt="logo"
                  style={{ height: 36, display: "block" }}
                />
              )}
            </div>
          )}

          <div style={{ lineHeight: headerOptions.lineHeight }}>
            {editingTitle ? (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingTitle(false);
                }}
                onBlur={() => setEditingTitle(false)}
                size="large"
                autoFocus
                style={{
                  fontFamily: headerOptions.fontFamily,
                  fontSize: headerOptions.fontSize,
                  fontWeight: 700,
                  color: headerOptions.titleColor,
                  WebkitTextFillColor: headerOptions.titleColor,
                  background: "transparent",
                  backgroundColor: "transparent",
                  border: "1px solid rgba(255,255,255,0.35)",
                  boxShadow: "none",
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
              />
            ) : (
              <Title
                level={3}
                style={{
                  color: headerOptions.titleColor,
                  margin: 0,
                  lineHeight: 1.1,
                  fontSize: headerOptions.fontSize,
                  fontFamily: headerOptions.fontFamily,
                }}
              >
                {title}
              </Title>
            )}

            <Text
              style={{
                color: headerOptions.subtitleColor,
                fontFamily: headerOptions.fontFamily,
              }}
            >
              {subtitle}
            </Text>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: "flex-end",
          }}
        >
          {actions && (
            <div style={{ marginBottom: rightSide ? 8 : 0 }}>{actions}</div>
          )}
          {rightSide &&
            headerOptions.headerImageDataUrl &&
            headerOptions.headerImageAlign === "right" && (
              <img
                src={getAbsoluteImageUrl(headerOptions.headerImageDataUrl)}
                alt="header"
                style={{
                  width: headerOptions.headerImageWidth,
                  height: "auto",
                  borderRadius: headerOptions.headerImageRadius,
                  display: "block",
                  marginLeft: "auto",
                }}
              />
            )}

          {rightSide &&
            headerOptions.logoUrl &&
            headerOptions.logoAlign === "right" && (
              <img
                src={getAbsoluteImageUrl(headerOptions.logoUrl)}
                alt="logo"
                style={{ height: 36, display: "block", marginLeft: "auto" }}
              />
            )}
        </div>
      </div>
    </Header>
  );
};

export default React.memo(HeaderSection);
